import { PurchaseOrdersRoute } from '@/features/procurement';
import { CRM_RECENT_ACTION_ROLES, isSupportedLocale } from '@erp/shared';
import type { WorkPath } from '@/app/work-tabs/work-path';
import {
  PartnerCustomersRoute,
  PartnerSuppliersRoute,
  PartnerProspectsRoute,
} from '@/features/partners';
import { ProductsRoute } from '@/features/products';
import { DocumentsRoute } from '@/features/documents';
import { groupWorkNavigation, workDomain } from './work-domains';
import { AppearanceProvider, useAppearance } from '@/features/appearance';
import { WorkArea } from '@/app/work-tabs/work-area';

import { changeLocale } from '@/lib/i18n';
import { IntroductionBanner } from '@/features/introduction';
import {
  BarChart3,
  Ban,
  Bell,
  BriefcaseBusiness,
  Building2,
  Calculator,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Contact,
  FileText,
  FileCheck2,
  FileSpreadsheet,
  FolderArchive,
  Factory,
  GitBranch,
  GitCompareArrows,
  Globe2,
  LayoutDashboard,
  Landmark,
  ListFilter,
  Menu,
  MapPin,
  Mail,
  MessageCircle,
  MessageSquareWarning,
  Megaphone,
  Package,
  PackagePlus,
  PackageSearch,
  ReceiptText,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Target,
  Tags,
  Truck,
  User,
  Users,
  WalletCards,
} from 'lucide-react';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { usePermissions, useSession } from '@/features/auth';
import { useNotificationStream } from '@/features/notifications';
import { SalesCtiOutcomeOverlay, ScreenPopOverlay } from '@/features/telephony';
import { ForcePasswordChange } from '@/features/users';
import { HelpPanel, OPEN_HELP_EVENT } from '@/features/help';
import { FeedbackDialog } from '@/features/feedback';
import { HeaderActions } from './header-actions';
import { FeatureRouteBoundary, useFeatureAvailability } from '@/features/platform-features';
import { recordFeedbackBreadcrumb, trackFeedbackRoute } from '@/lib/feedback-diagnostics';
import { cn } from '@/lib/utils';
import {
  filterNavigation,
  canAccessNavigationItem,
  pathMatches,
  resolveNavigationTarget,
  type NavItem,
} from './app-navigation';

function renderWorkCompanion(path: WorkPath): JSX.Element | null {
  if (path.family === 'products') return <ProductsRoute />;
  if (path.family === 'documents')
    return (
      <DocumentsRoute
        initialType={
          path.listPath === '/sales/orders'
            ? 'ORDER'
            : path.listPath === '/sales/quotes'
              ? 'QUOTE'
              : path.listPath === '/sales/invoices'
                ? 'INVOICE'
                : path.listPath === '/sales/delivery-notes'
                  ? 'DELIVERY_NOTE'
                  : path.listPath === '/sales/credit-notes'
                    ? 'CREDIT_NOTE'
                    : 'ALL'
        }
        titleKey={path.labelKey}
      />
    );
  if (path.family === 'purchases') return <PurchaseOrdersRoute />;
  if (path.listPath === '/partners/suppliers') return <PartnerSuppliersRoute />;
  if (path.listPath === '/partners/prospects') return <PartnerProspectsRoute />;
  if (path.family === 'partners') return <PartnerCustomersRoute />;
  return null;
}

const NAV_ITEMS: readonly NavItem[] = [
  { to: '/', icon: LayoutDashboard, labelKey: 'nav.dashboard', shortcut: 'd' },
  {
    to: '/returns',
    icon: MessageSquareWarning,
    labelKey: 'returns.title',
    access: { resource: 'RETURNS', action: 'READ' },
  },
  {
    to: '/picking',
    icon: PackageSearch,
    labelKey: 'picking.title',
    access: { resource: 'PICKING', action: 'READ' },
  },
  {
    to: '/inventory-sessions',
    icon: ClipboardCheck,
    labelKey: 'inventorySessions.title',
    access: { resource: 'INVENTORY_COUNTS', action: 'READ' },
  },
  {
    to: '/delivery-tours',
    icon: Truck,
    labelKey: 'tours.title',
    access: { resource: 'DELIVERY_TOURS', action: 'READ' },
  },
  {
    to: '/driver',
    icon: Truck,
    labelKey: 'tours.driverTitle',
    access: { resource: 'DELIVERY_TOURS', action: 'USE' },
  },
  {
    to: '/reusable-containers',
    icon: Package,
    labelKey: 'containers.title',
    access: { resource: 'REUSABLE_CONTAINERS', action: 'READ' },
  },
  {
    to: '/electronic-invoices',
    icon: FileCheck2,
    labelKey: 'electronicInvoices.title',
    access: { resource: 'ELECTRONIC_INVOICES', action: 'READ' },
  },
  {
    to: '/partners/customers',
    icon: Users,
    labelKey: 'nav.crm',
    shortcut: 'c',
    access: { resource: 'CUSTOMERS', action: 'READ' },
    children: [
      {
        to: '/crm/recent-actions',
        icon: ClipboardCheck,
        labelKey: 'crmRecentActions.title',
        access: { resource: 'CUSTOMERS', action: 'READ' },
        allowedRoles: CRM_RECENT_ACTION_ROLES,
      },
      {
        to: '/sales-assistant',
        icon: BriefcaseBusiness,
        labelKey: 'salesAssistant.cockpit.title',
        access: { resource: 'SALES_ASSISTANT', action: 'USE' },
      },
      {
        to: '/sales-assistant/analytics',
        icon: BarChart3,
        labelKey: 'salesAssistant.analytics.title',
        access: { resource: 'SALES_ASSISTANT', action: 'VIEW_OWN_TASKS' },
      },
      {
        to: '/partners/customers',
        icon: Building2,
        labelKey: 'nav.partnerCustomers',
        access: { resource: 'CUSTOMERS', action: 'READ' },
      },
      {
        to: '/partners/prospects',
        icon: Target,
        labelKey: 'nav.partnerProspects',
        access: { resource: 'CUSTOMERS', action: 'READ' },
      },
      {
        to: '/partners/map',
        labelKey: 'partnerMap.title',
        icon: MapPin,
        access: { resource: 'CUSTOMERS', action: 'READ' },
      },
      {
        to: '/partners',
        icon: Contact,
        labelKey: 'nav.partnerAll',
        access: { resource: 'CUSTOMERS', action: 'READ' },
      },
    ],
  },
  {
    to: '/sales/all',
    icon: FileText,
    labelKey: 'nav.salesOrderProcessing',
    shortcut: 'b',
    access: { resource: 'DOCUMENTS', action: 'READ' },
    children: [
      {
        to: '/sales/quotes',
        icon: ClipboardCheck,
        labelKey: 'nav.quotes',
        access: { resource: 'DOCUMENTS', action: 'READ' },
      },
      {
        to: '/sales/orders',
        icon: ShoppingCart,
        labelKey: 'nav.salesOrders',
        access: { resource: 'DOCUMENTS', action: 'READ' },
      },
      {
        to: '/sales/delivery-notes',
        icon: Truck,
        labelKey: 'nav.deliveryNotes',
        access: { resource: 'DOCUMENTS', action: 'READ' },
      },
      {
        to: '/sales/invoices',
        icon: ReceiptText,
        labelKey: 'nav.invoices',
        access: { resource: 'DOCUMENTS', action: 'READ' },
      },
      {
        to: '/sales/credit-notes',
        icon: FileText,
        labelKey: 'nav.creditNotes',
        access: { resource: 'DOCUMENTS', action: 'READ' },
      },
      {
        to: '/sales/all',
        icon: FileText,
        labelKey: 'nav.allTransactions',
        access: { resource: 'DOCUMENTS', action: 'READ' },
      },
      {
        to: '/legacy-documents',
        icon: FileText,
        labelKey: 'legacy.title',
        access: { resource: 'DOCUMENTS', action: 'READ' },
      },
    ],
  },
  {
    to: '/procurement/orders',
    icon: ShoppingCart,
    labelKey: 'nav.procurement',
    shortcut: 'e',
    access: { resource: 'PROCUREMENT', action: 'READ' },
    children: [
      {
        to: '/partners/suppliers',
        icon: PackageSearch,
        labelKey: 'nav.partnerSuppliers',
        access: { resource: 'CUSTOMERS', action: 'READ' },
      },
      {
        to: '/procurement/orders',
        icon: ShoppingCart,
        labelKey: 'nav.purchaseOrders',
        access: { resource: 'PROCUREMENT', action: 'READ' },
      },
      {
        to: '/procurement/goods-receipts',
        icon: PackagePlus,
        labelKey: 'nav.goodsReceipts',
        access: { resource: 'PROCUREMENT', action: 'READ' },
      },
      {
        to: '/procurement/invoices',
        icon: ReceiptText,
        labelKey: 'nav.vendorInvoices',
        access: { resource: 'PROCUREMENT', action: 'READ' },
      },
      {
        to: '/legacy-documents',
        icon: FileText,
        labelKey: 'legacy.title',
        access: { resource: 'PROCUREMENT', action: 'READ' },
      },
    ],
  },
  {
    to: '/products',
    icon: Package,
    labelKey: 'nav.productsInventory',
    shortcut: 'a',
    access: { resource: 'PRODUCTS', action: 'READ' },
    children: [
      {
        to: '/products',
        icon: Package,
        labelKey: 'nav.productsInventory',
        access: { resource: 'PRODUCTS', action: 'READ' },
      },
      {
        to: '/products/pricing',
        icon: Package,
        labelKey: 'pricing.title',
        access: { resource: 'PRICING', action: 'READ' },
      },
      {
        to: '/inventory/overview',
        icon: Package,
        labelKey: 'inventoryOverview.title',
        access: { resource: 'BATCHES', action: 'READ' },
      },
      {
        to: '/inventory/import',
        icon: FileSpreadsheet,
        labelKey: 'warehouseImport.nav',
        access: { resource: 'BATCHES', action: 'READ' },
      },
    ],
  },
  {
    to: '/product-development',
    icon: Factory,
    labelKey: 'nav.productionProductDevelopment',
    shortcut: 'm',
    access: { resource: 'PRODUCT_DEVELOPMENT', action: 'READ' },
    children: [
      {
        to: '/product-development',
        icon: LayoutDashboard,
        labelKey: 'nav.productDevelopmentOverview',
        access: { resource: 'PRODUCT_DEVELOPMENT', action: 'READ' },
      },
      {
        to: '/product-development/recipes',
        icon: PackageSearch,
        labelKey: 'nav.recipes',
        access: { resource: 'RECIPES', action: 'READ' },
      },
      {
        to: '/product-development/batch-scaling',
        icon: Calculator,
        labelKey: 'nav.batchScaling',
        access: { resource: 'RECIPES', action: 'READ' },
      },
      {
        to: '/product-development/declarations',
        icon: FileText,
        labelKey: 'nav.declarations',
        access: { resource: 'DECLARATIONS', action: 'READ' },
      },
      {
        to: '/product-development/declaration-catalog',
        icon: ClipboardCheck,
        labelKey: 'nav.declarationCatalog',
        access: { resource: 'DECLARATIONS', action: 'READ' },
      },
      {
        to: '/product-development/analysis',
        icon: BarChart3,
        labelKey: 'nav.analysisProfiles',
        access: { resource: 'ANALYSIS', action: 'READ' },
      },
      {
        to: '/product-development/analysis-parameters',
        icon: Settings,
        labelKey: 'nav.analysisParameters',
        access: { resource: 'ANALYSIS', action: 'READ' },
      },
      {
        to: '/product-development/labels',
        icon: Tags,
        labelKey: 'nav.labels',
        access: { resource: 'LABEL_MANAGEMENT', action: 'READ' },
      },
      {
        to: '/product-development/change-control',
        icon: GitCompareArrows,
        labelKey: 'nav.changeControl',
        access: { resource: 'CHANGE_CONTROL', action: 'READ' },
      },
      {
        to: '/manufacturing/production-orders',
        icon: Factory,
        labelKey: 'nav.productionOrders',
        access: { resource: 'MANUFACTURING', action: 'READ' },
      },
      {
        to: '/manufacturing/traceability',
        icon: GitBranch,
        labelKey: 'nav.traceability',
        access: { resource: 'TRACEABILITY', action: 'READ' },
      },
      {
        to: '/manufacturing/mrp',
        icon: Calculator,
        labelKey: 'nav.mrp',
        access: { resource: 'MRP', action: 'READ' },
      },
    ],
  },
  {
    to: '/finance/accounting',
    icon: Calculator,
    labelKey: 'nav.finance',
    shortcut: 'f',
    access: { resource: 'LEDGER', action: 'READ' },
    children: [
      {
        to: '/finance/accounting',
        icon: Calculator,
        labelKey: 'ledger.views.overview',
        access: { resource: 'LEDGER', action: 'READ' },
      },
      {
        to: '/finance/journal',
        icon: Calculator,
        labelKey: 'ledger.views.journal',
        access: { resource: 'LEDGER', action: 'READ' },
      },
      {
        to: '/finance/accounts',
        icon: Calculator,
        labelKey: 'ledger.views.accounts',
        access: { resource: 'LEDGER', action: 'READ' },
      },
      {
        to: '/finance/reports',
        icon: Calculator,
        labelKey: 'ledger.views.reports',
        access: { resource: 'LEDGER', action: 'READ' },
      },
      {
        to: '/finance/periods',
        icon: Calculator,
        labelKey: 'ledger.views.periods',
        access: { resource: 'LEDGER', action: 'READ' },
      },
      {
        to: '/finance/templates',
        icon: Calculator,
        labelKey: 'ledgerClosing.templates',
        access: { resource: 'LEDGER', action: 'READ' },
      },
      {
        to: '/finance/assets',
        icon: Calculator,
        labelKey: 'ledgerClosing.assets',
        access: { resource: 'LEDGER', action: 'READ' },
      },
      {
        to: '/finance/closing',
        icon: Calculator,
        labelKey: 'ledgerClosing.closing',
        access: { resource: 'LEDGER', action: 'READ' },
      },
      {
        to: '/finance/revaluations',
        icon: Calculator,
        labelKey: 'ledgerClosing.revaluations',
        access: { resource: 'LEDGER', action: 'READ' },
      },
      {
        to: '/finance/vat-rates',
        icon: Calculator,
        labelKey: 'fiscalFx.title',
        access: { resource: 'LEDGER', action: 'READ' },
      },
      {
        to: '/finance/vat-return',
        icon: Calculator,
        labelKey: 'vatReturn.title',
        access: { resource: 'LEDGER', action: 'READ' },
      },
      {
        to: '/finance/cashbook',
        icon: WalletCards,
        labelKey: 'ledgerCashbook.title',
        access: { resource: 'LEDGER', action: 'READ' },
      },
      {
        to: '/sales/invoices',
        icon: ReceiptText,
        labelKey: 'nav.invoices',
        access: { resource: 'DOCUMENTS', action: 'READ' },
      },
      {
        to: '/procurement/vendor-invoices',
        icon: ReceiptText,
        labelKey: 'nav.vendorInvoices',
        access: { resource: 'PROCUREMENT', action: 'READ' },
      },
      {
        to: '/finance/bank-accounts',
        icon: Landmark,
        labelKey: 'nav.bankAccounts',
        access: { resource: 'BANKING', action: 'READ' },
      },
      {
        to: '/finance/bank-transactions',
        icon: GitCompareArrows,
        labelKey: 'nav.bankTransactions',
        access: { resource: 'BANKING', action: 'READ' },
      },
      {
        to: '/finance/bank-reconciliation',
        icon: Calculator,
        labelKey: 'nav.bankReconciliation',
        access: { resource: 'BANKING', action: 'READ' },
      },
      {
        to: '/finance/open-items',
        icon: ReceiptText,
        labelKey: 'nav.openItems',
        access: { resource: 'BANKING', action: 'READ' },
      },
      {
        to: '/finance/payments',
        icon: WalletCards,
        labelKey: 'nav.payments',
        access: { resource: 'BANKING', action: 'READ' },
      },
      {
        to: '/finance/payment-plans',
        icon: WalletCards,
        labelKey: 'paymentPlans.title',
        access: { resource: 'LEDGER', action: 'READ' },
      },
      {
        to: '/finance/transfers',
        icon: WalletCards,
        labelKey: 'ledgerTransfers.title',
        access: { resource: 'BANKING', action: 'READ' },
      },
      {
        to: '/finance/dunning',
        icon: MessageSquareWarning,
        labelKey: 'nav.dunning',
        access: { resource: 'DUNNING', action: 'READ' },
      },
      {
        to: '/accounting/datev',
        icon: Calculator,
        labelKey: 'nav.datev',
        access: { resource: 'ACCOUNTING', action: 'EXPORT' },
      },
      {
        to: '/accounting/intrastat',
        icon: Truck,
        labelKey: 'nav.intrastat',
        access: { resource: 'ACCOUNTING', action: 'EXPORT' },
      },
    ],
  },
  {
    to: '/whatsapp',
    icon: MessageCircle,
    labelKey: 'nav.communicationDocuments',
    shortcut: 'w',
    access: { resource: 'WHATSAPP_MESSAGES', action: 'READ' },
    children: [
      {
        to: '/whatsapp',
        icon: MessageCircle,
        labelKey: 'nav.whatsapp',
        access: { resource: 'WHATSAPP_MESSAGES', action: 'READ' },
      },
      {
        to: '/communication/emails',
        icon: Mail,
        labelKey: 'mail.messages.title',
        accessAny: [
          { resource: 'MAIL_MESSAGES', action: 'READ' },
          { resource: 'MAIL_RECORDS', action: 'READ' },
        ],
      },
      {
        to: '/dms',
        icon: FolderArchive,
        labelKey: 'nav.dms',
        access: { resource: 'DMS', action: 'READ' },
      },
      {
        to: '/field',
        icon: ClipboardCheck,
        labelKey: 'questionnaires.nav.cockpit',
        access: { resource: 'QUESTIONNAIRES', action: 'USE' },
      },
      {
        to: '/field/runs',
        icon: FileText,
        labelKey: 'questionnaires.nav.runs',
        access: { resource: 'QUESTIONNAIRES', action: 'VIEW_OWN_RUNS' },
      },
    ],
  },
  {
    to: '/marketing',
    icon: Megaphone,
    labelKey: 'nav.marketing',
    shortcut: 'n',
    access: { resource: 'NEWSLETTER', action: 'READ' },
    children: [
      {
        to: '/marketing',
        icon: LayoutDashboard,
        labelKey: 'nav.newsletterOverview',
        access: { resource: 'NEWSLETTER', action: 'READ' },
      },
      {
        to: '/marketing/campaigns',
        icon: Megaphone,
        labelKey: 'nav.newsletterCampaigns',
        access: { resource: 'NEWSLETTER', action: 'UPDATE' },
      },
      {
        to: '/marketing/templates',
        icon: FileText,
        labelKey: 'nav.newsletterTemplates',
        access: { resource: 'NEWSLETTER', action: 'UPDATE' },
      },
      {
        to: '/marketing/audiences',
        icon: ListFilter,
        labelKey: 'nav.newsletterAudiences',
        access: { resource: 'NEWSLETTER', action: 'MANAGE' },
      },
      {
        to: '/marketing/imports',
        icon: FileSpreadsheet,
        labelKey: 'nav.newsletterImports',
        access: { resource: 'NEWSLETTER', action: 'MANAGE' },
      },
      {
        to: '/marketing/recipients',
        icon: Users,
        labelKey: 'nav.newsletterRecipients',
        access: { resource: 'NEWSLETTER', action: 'MANAGE' },
      },
      {
        to: '/marketing/consents',
        icon: ShieldCheck,
        labelKey: 'nav.newsletterConsents',
        access: { resource: 'NEWSLETTER', action: 'READ' },
      },
      {
        to: '/marketing/existing-customer',
        icon: FileCheck2,
        labelKey: 'nav.newsletterExistingCustomer',
        access: { resource: 'NEWSLETTER', action: 'APPROVE' },
      },
      {
        to: '/marketing/suppression',
        icon: Ban,
        labelKey: 'nav.newsletterSuppression',
        access: { resource: 'NEWSLETTER', action: 'APPROVE' },
      },
      {
        to: '/marketing/deliverability',
        icon: Globe2,
        labelKey: 'nav.newsletterDeliverability',
        access: { resource: 'NEWSLETTER', action: 'READ' },
      },
      {
        to: '/marketing/analytics',
        icon: BarChart3,
        labelKey: 'nav.newsletterAnalytics',
        access: { resource: 'NEWSLETTER', action: 'READ' },
      },
      {
        to: '/marketing/settings',
        icon: Settings,
        labelKey: 'nav.newsletterSettings',
        access: { resource: 'NEWSLETTER', action: 'MANAGE_SETTINGS' },
      },
    ],
  },
  {
    to: '/analytics',
    icon: BarChart3,
    labelKey: 'nav.analytics',
    shortcut: 'r',
    access: { resource: 'ANALYTICS', action: 'READ' },
    children: [
      {
        to: '/analytics',
        icon: BarChart3,
        labelKey: 'nav.analyticsOverview',
        access: { resource: 'ANALYTICS', action: 'READ' },
      },
      {
        to: '/analytics/employee-performance',
        icon: Users,
        labelKey: 'nav.employeePerformance',
        access: { resource: 'EMPLOYEE_PERFORMANCE', action: 'VIEW' },
      },
    ],
  },
  {
    to: '/settings',
    icon: Settings,
    labelKey: 'nav.administration',
    shortcut: 'k',
    children: [
      { to: '/settings', icon: Settings, labelKey: 'nav.settings' },
      { to: '/settings/appearance', icon: LayoutDashboard, labelKey: 'appearance.title' },
      { to: '/settings/profile', icon: User, labelKey: 'nav.profile' },
      { to: '/settings/security', icon: ShieldCheck, labelKey: 'nav.security' },
      { to: '/settings/notifications', icon: Bell, labelKey: 'nav.notifications' },
      {
        to: '/settings/operations-alerts',
        icon: Bell,
        labelKey: 'operationsAlerts.title',
        access: { resource: 'OPERATIONS', action: 'READ' },
      },
      {
        to: '/settings/company',
        icon: Building2,
        labelKey: 'nav.company',
        access: { resource: 'SETTINGS', action: 'READ' },
      },
      {
        to: '/settings/users',
        icon: Users,
        labelKey: 'nav.users',
        access: { resource: 'USERS', action: 'READ' },
      },
    ],
  },
];

/** Additional destinations for the sequential `g` keyboard shortcut. */
const SETTINGS_SHORTCUTS: readonly NavItem[] = [
  { to: '/settings', icon: Settings, labelKey: 'nav.settings', shortcut: 'k' },
  { to: '/settings/profile', icon: User, labelKey: 'nav.profile', shortcut: 'p' },
  { to: '/settings/security', icon: ShieldCheck, labelKey: 'nav.security', shortcut: 's' },
  {
    to: '/settings/users',
    icon: Users,
    labelKey: 'nav.users',
    shortcut: 'u',
    access: { resource: 'USERS', action: 'READ' },
  },
];

export function AppShell(): JSX.Element {
  const session = useSession();
  const user = session.data;
  if (!user) return <div aria-busy="true" />;
  return (
    <AppearanceProvider
      key={JSON.stringify([user.tenantId, user.id])}
      tenantId={user.tenantId}
      userId={user.id}
    >
      <AppShellContent />
    </AppearanceProvider>
  );
}

function AppShellContent(): JSX.Element {
  const { variant } = useAppearance();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  useEffect(() => {
    const locale = session.data?.locale;
    if (locale !== undefined && isSupportedLocale(locale)) changeLocale(locale);
  }, [session.data?.locale]);
  const { canAccess, permissions, isSuperAdmin } = usePermissions();
  const features = useFeatureAvailability();
  const [goPending, setGoPending] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const mobileNavTrigger = useRef<HTMLButtonElement>(null);
  const [helpOpen, setHelpOpen] = useState(() =>
    new URLSearchParams(window.location.search).has('help'),
  );
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    const openHelp = (): void => setHelpOpen(true);
    window.addEventListener(OPEN_HELP_EVENT, openHelp);
    return () => window.removeEventListener(OPEN_HELP_EVENT, openHelp);
  }, []);

  // Hält die Echtzeitverbindung offen, solange die Anwendung geöffnet ist.
  useNotificationStream(features.isEnabled('notifications'));

  const visibleNavItems = useMemo(
    () => filterNavigation(NAV_ITEMS, canAccess, features.isPathEnabled, session.data?.roleKey),
    [canAccess, features, session.data?.roleKey],
  );

  const visibleSettingsItems = useMemo(
    () =>
      SETTINGS_SHORTCUTS.filter(
        (item) => item.access === undefined || canAccess(item.access.resource, item.access.action),
      ).filter((item) => features.isPathEnabled(item.to)),
    [canAccess, features],
  );

  const activeNavigation = useMemo(() => {
    const sales = visibleNavItems.find((item) => item.labelKey === 'nav.salesOrderProcessing');
    if (pathMatches(location.pathname, '/documents') && sales !== undefined) {
      return {
        parent: sales,
        child: sales.children?.find((child) => child.labelKey === 'nav.allTransactions'),
      };
    }

    let best: { parent: NavItem; child?: NavItem; length: number } | null = null;
    for (const parent of visibleNavItems) {
      if (pathMatches(location.pathname, parent.to) && parent.children === undefined) {
        best = { parent, length: parent.to.length };
      }
      for (const child of parent.children ?? []) {
        if (
          canAccessNavigationItem(child, canAccess) &&
          pathMatches(location.pathname, child.to) &&
          (best === null || child.to.length > best.length)
        ) {
          best = { parent, child, length: child.to.length };
        }
      }
    }
    return best;
  }, [canAccess, location.pathname, visibleNavItems]);

  const activeGroup = activeNavigation?.parent.children ? activeNavigation.parent.to : null;
  const [openGroup, setOpenGroup] = useState<string | null>(activeGroup);

  // Route changes replace the open group; manual expansion also selects just one group.
  useEffect(() => {
    setOpenGroup(activeGroup);
  }, [activeGroup, location.pathname]);

  useEffect(() => {
    if (!features.isEnabled('feedback')) return;
    trackFeedbackRoute(location.pathname, location.search);
  }, [features, location.pathname, location.search]);

  useEffect(() => {
    if (!features.isEnabled('feedback')) return;
    const trackSemanticAction = (event: MouseEvent): void => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const action = target.closest<HTMLElement>('[data-feedback-action]');
      const actionId = action?.dataset['feedbackAction'];
      if (action === null || actionId === undefined) return;
      const result = action.dataset['feedbackResult'];
      recordFeedbackBreadcrumb({
        type: 'ACTION',
        actionId,
        actionLabelKey: action.dataset['feedbackLabelKey'] ?? null,
        entityType: action.dataset['feedbackEntityType'] ?? null,
        entityId: action.dataset['feedbackEntityId'] ?? null,
        result:
          result === 'FAILED' || result === 'CANCELLED' || result === 'SUCCESS'
            ? result
            : 'SUCCESS',
        errorTrackingId: null,
      });
    };
    document.addEventListener('click', trackSemanticAction, true);
    return () => document.removeEventListener('click', trackSemanticAction, true);
  }, [features]);

  // Der mobile Drawer schließt sich, sobald eine andere Seite erreicht wird.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Ab `lg` übernimmt die feste Seitenleiste; ein offener Drawer würde sonst als Overlay hängen bleiben.
  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1024px)');
    const sync = (): void => {
      if (desktop.matches) {
        setMobileNavOpen(false);
      }
    };

    sync();
    desktop.addEventListener('change', sync);
    return () => desktop.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      const isEditable =
        target !== null &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (
        features.isEnabled('feedback') &&
        event.altKey &&
        event.shiftKey &&
        event.key.toLocaleLowerCase() === 'f'
      ) {
        event.preventDefault();
        setFeedbackOpen(true);
        return;
      }
      if (isEditable || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      if (event.key === '?') {
        event.preventDefault();
        setHelpOpen(true);
        return;
      }

      if (event.key === 'g') {
        setGoPending(true);
        return;
      }

      if (!goPending) {
        return;
      }

      setGoPending(false);
      const item = [...visibleNavItems, ...visibleSettingsItems].find(
        (entry) => entry.shortcut === event.key,
      );
      if (item !== undefined) {
        const target = resolveNavigationTarget(
          item,
          canAccess,
          features.isPathEnabled,
          session.data?.roleKey,
        );
        if (target !== null) {
          event.preventDefault();
          navigate(target);
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    canAccess,
    features,
    goPending,
    navigate,
    visibleNavItems,
    visibleSettingsItems,
    session.data?.roleKey,
  ]);

  const linkClassName = ({ isActive }: { isActive: boolean }): string =>
    cn(
      'flex min-h-11 items-center gap-2 rounded-md px-3 text-sm transition-colors',
      isActive
        ? 'bg-secondary text-secondary-foreground'
        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
    );

  const renderItem = (item: NavItem): JSX.Element | null => {
    const { to, icon: Icon, labelKey, children } = item;

    if (children === undefined) {
      return (
        <li key={to}>
          <NavLink
            to={to}
            end={to === '/'}
            title={t(labelKey)}
            className={linkClassName}
            onClick={() => {
              setOpenGroup(null);
              setMobileNavOpen(false);
            }}
          >
            <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t(labelKey)}
          </NavLink>
        </li>
      );
    }

    const isOpen = openGroup === to;
    const visibleChildren = children.filter((child) => canAccessNavigationItem(child, canAccess));
    const groupTarget = resolveNavigationTarget(
      item,
      canAccess,
      features.isPathEnabled,
      session.data?.roleKey,
    );
    if (groupTarget === null) return null;
    const groupActive = visibleChildren.some((child) => pathMatches(location.pathname, child.to));

    return (
      <li key={to}>
        <div className="flex items-center gap-1">
          <NavLink
            to={groupTarget}
            title={t(labelKey)}
            className={({ isActive }) =>
              cn(linkClassName({ isActive: isActive || groupActive }), 'min-w-0 flex-1')
            }
            onClick={() => {
              setOpenGroup(to);
              setMobileNavOpen(false);
            }}
          >
            <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="truncate">{t(labelKey)}</span>
          </NavLink>
          <button
            type="button"
            aria-expanded={isOpen}
            aria-controls={`nav-group-${to}`}
            aria-label={t(isOpen ? 'nav.collapseGroup' : 'nav.expandGroup', { name: t(labelKey) })}
            title={t(isOpen ? 'nav.collapseGroup' : 'nav.expandGroup', { name: t(labelKey) })}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            onClick={() => setOpenGroup((current) => (current === to ? null : to))}
          >
            <ChevronDown
              className={cn('h-4 w-4 transition-transform duration-200', isOpen && 'rotate-180')}
              aria-hidden="true"
            />
          </button>
        </div>
        {isOpen ? (
          <ul
            id={`nav-group-${to}`}
            className="mt-1 flex flex-col gap-1 border-s border-border ps-3 duration-200 animate-in fade-in-0 slide-in-from-top-1"
          >
            {visibleChildren.map((child) => (
              <li key={`${to}-${child.labelKey}`}>
                <NavLink
                  to={child.to}
                  end
                  title={t(child.labelKey)}
                  className={linkClassName}
                  onClick={() => {
                    setOpenGroup(to);
                    setMobileNavOpen(false);
                  }}
                >
                  <child.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="truncate">{t(child.labelKey)}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        ) : null}
      </li>
    );
  };

  const navigation = variant.Navigation ? (
    <variant.Navigation
      pathname={location.pathname}
      activeDomain={workDomain(location.pathname)}
      onNavigate={() => setMobileNavOpen(false)}
      groups={groupWorkNavigation(visibleNavItems).map((group) => ({
        id: group.id,
        label: t(`workAreas.${group.id}`),
        items: [
          ...new Map(
            group.items
              .flatMap((item) => [item, ...(item.children ?? [])])
              .flatMap((item) => {
                const target = resolveNavigationTarget(
                  item,
                  canAccess,
                  features.isPathEnabled,
                  session.data?.roleKey,
                );
                return target === null
                  ? []
                  : [[target, { to: target, label: t(item.labelKey), icon: item.icon }] as const];
              }),
          ).values(),
        ],
      }))}
    />
  ) : (
    <nav aria-label={t('nav.label')} className="px-2 pb-4">
      <ul className="flex flex-col gap-1">
        {!variant.groupedNavigation
          ? visibleNavItems.map(renderItem)
          : groupWorkNavigation(visibleNavItems).map((group) => (
              <li key={group.id} className="work-domain" data-domain={group.id}>
                <details open={variant.composition !== 'folio'}>
                  <summary className="work-domain-title">{t(`workAreas.${group.id}`)}</summary>
                  <ul>{group.items.map(renderItem)}</ul>
                </details>
              </li>
            ))}
      </ul>
    </nav>
  );

  const brand = (
    <span className="flex shrink-0 items-center gap-2">
      <span className="flex h-8 min-w-10 shrink-0 px-1 items-center justify-center rounded-md bg-primary text-xs font-semibold text-primary-foreground">
        {t('app.name')}
      </span>
    </span>
  );

  return (
    <div
      data-appearance={variant.id}
      data-domain={workDomain(location.pathname)}
      className={cn(
        variant.className,
        'erp-shell flex min-h-dvh flex-col bg-background pt-[var(--operating-banner-height,0px)] lg:flex-row',
      )}
    >
      <IntroductionBanner />
      {/* Der Drawer ersetzt die Seitenleiste unterhalb von lg, damit sie den Inhalt nie verdrängt. */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent
          side="left"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            mobileNavTrigger.current?.focus();
          }}
          closeLabel={t('common.close')}
          className="gap-0 p-0 [&>button]:top-1 [&>button]:h-11 [&>button]:w-11"
          aria-label={t('nav.label')}
        >
          <div className="flex h-14 shrink-0 items-center gap-2 px-4">
            <SheetTitle className="flex-1 truncate">{brand}</SheetTitle>
          </div>
          <div className="flex-1 overflow-y-auto overscroll-contain pb-safe-bottom">
            {navigation}
          </div>
        </SheetContent>
      </Sheet>

      <aside className="erp-sidebar hidden shrink-0 border-e border-border lg:sticky lg:top-[var(--operating-banner-height,0px)] lg:block lg:h-[calc(100dvh-var(--operating-banner-height,0px))] lg:w-60 lg:overflow-y-auto">
        <div className="flex h-14 items-center gap-2 px-4">
          <span className="text-sm font-semibold tracking-tight">{t('app.name')}</span>
          <span className="text-xs text-muted-foreground">{t('app.tagline')}</span>
        </div>
        {navigation}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-[var(--operating-banner-height,0px)] z-40 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-background ps-[max(.5rem,env(safe-area-inset-left))] pe-[max(.5rem,env(safe-area-inset-right))] sm:ps-[max(1rem,env(safe-area-inset-left))] sm:pe-[max(1rem,env(safe-area-inset-right))]">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-11 w-11 shrink-0 md:h-11 md:w-11 lg:hidden"
            ref={mobileNavTrigger}
            aria-label={t('nav.openMenu')}
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen(true)}
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </Button>
          <span className="shrink-0 lg:hidden">{brand}</span>

          <div className="ms-auto flex shrink-0 items-center gap-1">
            <HeaderActions
              onHelp={() => setHelpOpen(true)}
              onFeedback={() => setFeedbackOpen(true)}
            />
          </div>
        </header>
        <main className="min-w-0 flex-1 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] md:p-6 md:pb-[max(1.5rem,env(safe-area-inset-bottom))]">
          {activeNavigation === null || location.pathname === '/' ? null : (
            <nav aria-label={t('nav.breadcrumb')} className="mb-4 text-sm text-muted-foreground">
              <ol className="flex min-w-0 flex-wrap items-center gap-1">
                <li className="truncate">{t(activeNavigation.parent.labelKey)}</li>
                {activeNavigation.child === undefined ? null : (
                  <>
                    <li aria-hidden="true">
                      <ChevronRight className="h-4 w-4" />
                    </li>
                    <li className="truncate font-medium text-foreground" aria-current="page">
                      {t(activeNavigation.child.labelKey)}
                    </li>
                  </>
                )}
              </ol>
            </nav>
          )}
          <FeatureRouteBoundary>
            <Suspense
              fallback={
                <div className="min-h-48 animate-pulse rounded-lg bg-muted" aria-busy="true" />
              }
            >
              <WorkArea
                renderCompanion={renderWorkCompanion}
                isPathAvailable={(pathname) =>
                  features.isPathEnabled(pathname) &&
                  (pathname.startsWith('/partners')
                    ? canAccess('CUSTOMERS', 'READ')
                    : pathname.startsWith('/products')
                      ? canAccess('PRODUCTS', 'READ')
                      : pathname.startsWith('/procurement')
                        ? canAccess('PROCUREMENT', 'READ')
                        : canAccess('DOCUMENTS', 'READ'))
                }
              />
            </Suspense>
          </FeatureRouteBoundary>
        </main>
      </div>

      <ForcePasswordChange />
      {features.isEnabled('telephony_ringover') ? <ScreenPopOverlay /> : null}
      {features.isEnabled('telephony_ringover') ? <SalesCtiOutcomeOverlay /> : null}
      <HelpPanel
        open={helpOpen}
        onOpenChange={setHelpOpen}
        pathname={location.pathname}
        tenantLocale={session.data?.tenantLocale}
        permissions={permissions}
        isSuperAdmin={isSuperAdmin}
        isFeaturePathAvailable={features.isPathEnabled}
      />
      {features.isEnabled('feedback') ? (
        <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      ) : null}
    </div>
  );
}
